import { useEffect, useMemo } from "react";

import { yupResolver } from "@hookform/resolvers/yup";
import { isAxiosError } from "axios";
import { FormProvider, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { ObjectSchema } from "yup";

import { Button } from "src/base-components/Button";
import { FloatingWindowsProvider } from "src/base-components/FloatingWindow/FloatingWindowsProvider";
import { type FileCategoryBase } from "src/cases/caseView/FileCategory";
import {
  getStatusOptions,
  parseStatusKey,
} from "src/cases/common/statusOptions";
import { CreateCaseFormRouter } from "src/cases/createCase/CreateCaseFormRouter";
import { CreateCaseSidebar } from "src/cases/createCase/CreateCaseSidebar";
import {
  buildCreateCaseSchema,
  CreateCaseFormValues,
} from "src/cases/createCase/createCaseSteps";
import { useCreateCase } from "src/cases/queries/cases";
import { useCasesClient } from "src/cases/queries/client";
import { caseQueuesKeys, useCaseQueue } from "src/cases/queries/queues";
import { useResolvedTemplateVersion } from "src/cases/queries/useResolvedTemplateVersion";
import {
  CaseTemplatesWrapper,
  MENUBAR_WIDTH_KEY,
} from "src/cases/templates/CaseTemplatesWrapper";
import {
  AttributesValue,
  CaseCreateRequest,
  CaseCreateRequestIncludedInner,
  CaseEnvironment,
  JsonApiResourceObjectCaseTemplateVersionAttributesBaseModel,
  UpdatedPropertiesValue,
} from "src/clients/case-management";
import { WorkspaceSimpleRoleType } from "src/clients/taktile-api";
import { toastActions } from "src/design-system/Toast/utils";
import { useCapabilities } from "src/hooks/useCapabilities";
import { useDocumentTitle } from "src/hooks/useDocumentTitle";
import { useEnvironment } from "src/hooks/useEnvironment";
import { PageHeader } from "src/layout/PageHeader";
import { queryClient } from "src/queryClient";
import { useWorkspaceContext } from "src/router/routerContextHooks";
import {
  CasesPageParams,
  getBaseUrl,
  getUrlToCaseQueuePage,
  getUrlToCaseViewPage,
  getUrlToCreateCasePage,
} from "src/router/urls";
import { useCurrentUserId } from "src/store/AuthStore";
import { useParamsDecode } from "src/utils/useParamsDecode";

const SOURCE_PROPERTY_KEY = "source";
const EXCLUDED_PROPERTY_KEYS = new Set(["source", "status", "sub_status"]);

const CREATE_CASE_TOAST_ID = "create-case";
const POLL_INTERVAL_MS = 500;
const POLL_TIMEOUT_MS = 60_000;

const buildCaseCreateRequest = (
  values: CreateCaseFormValues,
  env: CaseEnvironment,
  queueId: string,
  templateId: string,
  templateVersionId: string,
): CaseCreateRequest => {
  const casePropertiesAttributes = Object.fromEntries(
    Object.entries({
      status: values.status,
      sub_status: values.sub_status,
      source: values.source,
      ...values.case_properties,
    }).filter(
      ([, value]) => value !== undefined && value !== null && value !== "",
    ),
  );

  const included: CaseCreateRequestIncludedInner[] = [
    {
      type: "additional_inputs" as const,
      attributes: {} as { [key: string]: AttributesValue },
    },
    {
      type: "case_properties" as const,
      id: "case_properties",
      attributes: casePropertiesAttributes as {
        [key: string]: UpdatedPropertiesValue;
      },
    },
  ];

  for (const [categoryKey, fileIds] of Object.entries(
    values.file_category_files,
  )) {
    if (fileIds.length > 0) {
      included.push({
        type: "file_categories" as const,
        attributes: { file_ids: fileIds, category_key: categoryKey },
      });
    }
  }

  return {
    data: {
      type: "cases" as const,
      attributes: {
        env,
        comment: values.comment || null,
      },
      relationships: {
        queue: { data: { type: "queues", id: queueId } },
        template: { data: { type: "case_templates", id: templateId } },
        template_version: {
          data: { type: "case_template_versions", id: templateVersionId },
        },
        additional_inputs: {
          data: { type: "additional_inputs", id: "additional_inputs" },
        },
        case_properties: {
          data: { type: "case_properties", id: "case_properties" },
        },
        ...(values.assignee_id && {
          assignee: { data: { type: "assignees", id: values.assignee_id } },
        }),
      },
    },
    included,
  };
};

const showCreateCaseError = () => {
  toastActions.dismiss(CREATE_CASE_TOAST_ID);
  toastActions.failure({
    title: "Case could not be created",
    description:
      "Something went wrong while creating the case. Try again or contact support if the issue persists",
  });
};

const pollForCase = async (
  apiClient: ReturnType<typeof useCasesClient>,
  caseId: string,
  queueName: string,
  caseViewUrl: string,
  workspaceId: string,
  queueId: string,
): Promise<void> => {
  if (!apiClient) return;

  const startTime = Date.now();

  const poll = async (): Promise<void> => {
    try {
      await apiClient.getCaseApiV1CasesIdGet({ id: caseId });

      await queryClient.refetchQueries({
        queryKey: caseQueuesKeys.get(workspaceId, queueId),
      });

      toastActions.dismiss(CREATE_CASE_TOAST_ID);
      toastActions.success({
        title: "Case created successfully",
        description: `Case has been added to ${queueName} queue`,
        actionText: "View case",
        onActionClick: () => {
          window.location.assign(caseViewUrl);
        },
      });
    } catch (error) {
      if (Date.now() - startTime > POLL_TIMEOUT_MS) {
        showCreateCaseError();
        return;
      }

      if (
        isAxiosError(error) &&
        (error.response?.status === 404 || error.response?.status === 409)
      ) {
        await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
        return poll();
      }

      showCreateCaseError();
    }
  };

  return poll();
};

type TemplateVersion =
  | JsonApiResourceObjectCaseTemplateVersionAttributesBaseModel
  | undefined;

const useSourceOptions = (version: TemplateVersion) => {
  return useMemo(() => {
    if (!version) return undefined;

    const sourceProperty =
      version.attributes.case_properties?.[SOURCE_PROPERTY_KEY];
    if (!sourceProperty || sourceProperty.type !== "enum") return undefined;

    return sourceProperty.values.map((v) => ({
      key: v.value,
      value: v.display_name || v.value,
    }));
  }, [version]);
};

const useFileCategories = (version: TemplateVersion): FileCategoryBase[] => {
  return useMemo(() => {
    if (!version?.attributes.file_categories) return [];

    return Object.entries(version.attributes.file_categories).map(
      ([key, def]) => ({
        category_key: key,
        display_name: def.display_name,
        description: def.description ?? undefined,
      }),
    );
  }, [version]);
};

const useCaseProperties = (version: TemplateVersion) => {
  return useMemo(() => {
    if (!version?.attributes.case_properties) return undefined;

    return Object.fromEntries(
      Object.entries(version.attributes.case_properties).filter(
        ([key]) => !EXCLUDED_PROPERTY_KEYS.has(key),
      ),
    );
  }, [version]);
};

const CreateCaseContent: React.FC = () => {
  const { queue_id: queueId, wsId, orgId } = useParamsDecode<CasesPageParams>();
  const navigate = useNavigate();
  const [env] = useEnvironment();
  const apiClient = useCasesClient();
  const createCase = useCreateCase();

  const { workspace } = useWorkspaceContext();
  const currentUserId = useCurrentUserId();
  const { workspaceRole } = useCapabilities();
  const isAdmin = workspaceRole === WorkspaceSimpleRoleType.ADMIN;

  const { data: queue } = useCaseQueue(queueId);
  const { version: resolvedVersion } = useResolvedTemplateVersion(queue);
  const sourceOptions = useSourceOptions(resolvedVersion);
  const fileCategories = useFileCategories(resolvedVersion);
  const caseProperties = useCaseProperties(resolvedVersion);
  const statusProperty = resolvedVersion?.attributes.case_properties?.status;

  const defaultStatus = useMemo(() => {
    const options = getStatusOptions(statusProperty);
    if (options.length === 0) return undefined;
    return parseStatusKey(options[0].key);
  }, [statusProperty]);

  const queuePageUrl = getUrlToCaseQueuePage({ orgId, wsId, queueId });

  const schema = useMemo(
    () =>
      buildCreateCaseSchema(
        caseProperties,
        !!sourceOptions,
        defaultStatus !== undefined,
      ),
    [caseProperties, sourceOptions, defaultStatus],
  );

  const form = useForm<CreateCaseFormValues>({
    mode: "onChange",
    defaultValues: {
      file_category_files: {},
      case_properties: {},
    },
    resolver: yupResolver<CreateCaseFormValues>(
      schema as ObjectSchema<CreateCaseFormValues>,
    ),
  });

  useEffect(() => {
    const defaultAssignee = !isAdmin ? currentUserId : undefined;

    if (defaultStatus || defaultAssignee) {
      form.reset(
        {
          ...form.getValues(),
          ...(defaultStatus && {
            status: defaultStatus.status,
            sub_status: defaultStatus.subStatus,
          }),
          ...(defaultAssignee && { assignee_id: defaultAssignee }),
        },
        { keepDirtyValues: true },
      );
    }
  }, [defaultStatus, currentUserId, isAdmin, form]);

  const onSubmit = async (values: CreateCaseFormValues) => {
    if (!resolvedVersion || !queue?.default_template_version) return;

    const templateId = queue.default_template_version.template.id;
    const request = buildCaseCreateRequest(
      values,
      env as CaseEnvironment,
      queueId,
      templateId,
      resolvedVersion.id,
    );

    toastActions.loading({
      id: CREATE_CASE_TOAST_ID,
      title: "Creating case...",
      description: `Creating new case for ${queue.name} queue`,
      isDismissible: false,
    });

    try {
      const response = await createCase.mutateAsync(request);

      const selfLink = response.links.self;
      const caseId = selfLink.split("/").pop();

      navigate(queuePageUrl);

      if (caseId) {
        const caseViewUrl =
          getBaseUrl() + getUrlToCaseViewPage(orgId, wsId, caseId);
        pollForCase(
          apiClient,
          caseId,
          queue.name,
          caseViewUrl,
          workspace.id,
          queueId,
        );
      }
    } catch {
      showCreateCaseError();
    }
  };

  return (
    <div className="decideScrollbar flex h-full flex-col bg-gray-50">
      <PageHeader
        backLink={queuePageUrl}
        breadcrumbs={[
          {
            key: "cases-page",
            breadcrumb: (
              <PageHeader.Breadcrumb
                dataLoc="cases-breadcrumb"
                href={getUrlToCaseQueuePage({ wsId, orgId })}
                label="Cases"
                hideIcon
              />
            ),
          },
          {
            key: "queue-page",
            breadcrumb: (
              <PageHeader.Breadcrumb
                dataLoc="queue-breadcrumb"
                href={queuePageUrl}
                label={queue?.name ?? ""}
              />
            ),
          },
          {
            key: "create-case",
            breadcrumb: (
              <PageHeader.Breadcrumb
                dataLoc="create-case-breadcrumb"
                href={getUrlToCreateCasePage({ wsId, orgId, queueId })}
                label="Create a Case"
                active
              />
            ),
          },
        ]}
        sideContent={
          <div className="flex items-center gap-x-3 pr-3">
            <Button
              dataLoc="cancel-create-case-button"
              size="sm"
              variant="secondary"
              onClick={() => navigate(queuePageUrl)}
            >
              Cancel
            </Button>
            <Button
              dataLoc="create-case-submit-button"
              disabled={!form.formState.isValid || createCase.isPending}
              size="sm"
              onClick={form.handleSubmit(onSubmit)}
            >
              Create a Case
            </Button>
          </div>
        }
      />
      <FormProvider {...form}>
        <CaseTemplatesWrapper>
          <div
            className="flex-none"
            style={{ width: `var(${MENUBAR_WIDTH_KEY})` }}
          >
            <CreateCaseSidebar />
          </div>
          <div className="isolate w-194">
            <CreateCaseFormRouter
              caseProperties={caseProperties}
              fileCategories={fileCategories}
              queueId={queueId}
              sourceOptions={sourceOptions}
              statusProperty={statusProperty}
            />
          </div>
        </CaseTemplatesWrapper>
      </FormProvider>
    </div>
  );
};

export const CreateCasePage: React.FC = () => {
  useDocumentTitle(["Create a Case", "Cases"]);
  return (
    <FloatingWindowsProvider>
      <CreateCaseContent />
    </FloatingWindowsProvider>
  );
};
